// Height reporting for the iframe embed. The embed's other cross-frame channel — the host handing
// its theme in — is `theme.ts`.
//
// The ZynoTalk lander hosts the signup wizard in an iframe, and an iframe has no intrinsic height —
// it would otherwise be a fixed box with its own inner scrollbar. So the embed measures itself and
// posts its height to the parent, which resizes the frame (see the listener in that site's
// src/pages/get-started.astro).
//
// Only the standalone embedded app calls this. When the SPA mounts the same wizard inside the suite
// shell, it lays out normally and there is no parent to talk to.

import { onBeforeUnmount, onMounted } from 'vue';

const MESSAGE_TYPE = 'zs:signup:height';
const SCROLL_MESSAGE_TYPE = 'zs:signup:scroll';

/**
 * Set on `<html>` for as long as this app is reporting its height to a host.
 *
 * Sizing anything against the viewport is a bug in that mode: the frame's height is whatever we last
 * told the host it was, so `100vh` measures our own content and any `vh`-sized box grows itself on
 * every report (see `.su-signwell` in `signup.styles.css`). CSS can't ask whether it is framed, so
 * the reporter says so.
 */
const FRAMED_ATTRIBUTE = 'data-su-framed';

/**
 * Put the top of the wizard back in view after a step change — a long step (equipment, review) would
 * otherwise drop you into the middle of the next one, wherever the old scroll position happened to
 * land.
 *
 * Framed, the iframe is sized to its content and never scrolls: only the host page can move, so we
 * ask it to. A host needs a listener for `zs:signup:scroll` alongside the height one for this to do
 * anything there; standalone and inside the SPA shell it works on its own.
 */
export function scrollWizardToTop() {
    if (window.parent !== window) {
        window.parent.postMessage({ type: SCROLL_MESSAGE_TYPE }, '*');
        return;
    }

    const root = document.getElementById('zyno-signup');
    if (root) root.scrollIntoView({ block: 'start' });
    else window.scrollTo({ top: 0 });
}

export function useEmbedHeightReporter() {
    let observer: ResizeObserver | null = null;
    let lastHeight = 0;

    function report() {
        // scrollHeight over getBoundingClientRect: the latter reports the *visible* box, which inside
        // a too-short iframe is the frame's own height — the measurement would then never grow.
        const height = document.documentElement.scrollHeight;
        // Sub-pixel churn from font loading and transitions would otherwise post on every frame.
        if (Math.abs(height - lastHeight) < 2) return;
        lastHeight = height;
        // Targeted at '*' deliberately: the embed doesn't know which site framed it (the ZynoTalk
        // lander today, another product's lander next), and the payload is a single integer with no
        // secrets. The *parent* checks the origin, which is the direction that matters.
        window.parent?.postMessage({ type: MESSAGE_TYPE, height }, '*');
    }

    onMounted(() => {
        // Not framed — nothing to report to.
        if (window.parent === window) return;
        // Before the first measurement, so nothing is ever measured against a viewport that is really
        // our own reported height.
        document.documentElement.setAttribute(FRAMED_ATTRIBUTE, '');
        observer = new ResizeObserver(report);
        observer.observe(document.documentElement);
        report();
    });

    onBeforeUnmount(() => {
        observer?.disconnect();
        observer = null;
        document.documentElement.removeAttribute(FRAMED_ATTRIBUTE);
    });
}
